import { Router } from '@angular/router';
import { BaseNotifier } from '../../../shared/notifiers/base-notifier';
import { Ref } from '../../../shared/providers/ref';
import { AuthRepositoryProvider } from '../../../shared/providers/repository-providers';
import { AuthState, initialAuthState } from './auth-state';
import {
  IAuthRepository,
  LoginEntity,
  ResponseValueObject,
} from '@gishub/domain';
import { SessionTimeoutService } from '../../../shared/services/session-timeout-service';
import { SessionCleanupService } from '../../../shared/services/session-cleanup-service';
import { SanitizeUtil } from '../../../shared/utils/sanitize-util';

/**
 * Notifier de auth — toda la reactividad y la lógica de sesión viven aquí.
 *
 * La sesión (accessToken + datos de usuario) vive SOLO en memoria por diseño
 * (contrato §1). El refresh token es una cookie httponly gestionada por el
 * backend: el frontend jamás lo lee, lo guarda ni lo envía. En un reload la
 * sesión se rehidrata vía POST /auth/refresh (cookie).
 */
export class AuthNotifier extends BaseNotifier<AuthState> {
  private logoutInProgress = false;

  /** Indica si la sesión ya fue establecida/rehidratada en esta vida de la app. */
  private bootstrapDone = false;
  private validationPromise: Promise<void> | null = null;

  constructor(ref: Ref) {
    super(ref, initialAuthState);

    const timeout = this.ref.read(SessionTimeoutService);
    timeout.setOnLogoutHandler(() => this.logout());
    timeout.setOnContinueHandler(() => {
      void this.refreshSession();
    });

    // La sesión es solo memoria (contrato §1): al arrancar se rehidrata en
    // silencio desde la cookie httponly vía POST /auth/refresh.
    setTimeout(() => void this.ensureSessionValidated(), 0);
  }

  /** Acceso al repositorio de auth a través del RepositoryProvider (capa infra). */
  private get repository(): IAuthRepository {
    return this.ref.read(AuthRepositoryProvider).instance;
  }

  private get router(): Router {
    return this.ref.read(Router);
  }

  private get sessionTimeout(): SessionTimeoutService {
    return this.ref.read(SessionTimeoutService);
  }

  private get sessionCleanup(): SessionCleanupService {
    return this.ref.read(SessionCleanupService);
  }

  setUsername(username: string): void {
    // Sanea la entrada en el trío (protección contra inyección HTML/script);
    // el componente no conoce utils.
    this.updateState((prev) => ({ ...prev, username: SanitizeUtil.sanitizeInput(username), error: null }));
  }

  setPassword(password: string): void {
    this.updateState((prev) => ({ ...prev, password, error: null }));
  }

  setLoading(loading: boolean): void {
    this.updateState((prev) => ({ ...prev, loading }));
  }

  setError(error: string | null): void {
    this.updateState((prev) => ({ ...prev, error }));
  }

  /** Sesión completa tras login (data de POST /auth/login). */
  setLoginResult(login: LoginEntity): void {
    this.updateState((prev) => {
      return {
        ...prev,
        isAuthenticated: true,
        loading: false,
        username: login.username ?? prev.username,
        fullName: login.fullName,
        email: login.email,
        id: login.id,
        isSuperadmin: login.isSuperadmin,
        roleType: login.roleType,
        roleId: login.roleId,
        roleName: login.roleName,
        roleLevel: login.roleLevel ?? null,
        jti: login.jti,
        familyId: login.familyId,
        accessToken: login.accessToken,
        error: null,
      };
    });
  }

  /** Rehidratación/rotación tras POST /auth/refresh (data parcial §2). */
  setRefreshResult(login: LoginEntity): void {
    this.updateState((prev) => {
      return {
        ...prev,
        isAuthenticated: true,
        id: login.id ?? prev.id,
        username: login.username ?? prev.username,
        fullName: login.fullName ?? prev.fullName,
        email: login.email ?? prev.email,
        isSuperadmin: login.isSuperadmin ?? prev.isSuperadmin,
        roleType: login.roleType ?? prev.roleType,
        roleId: login.roleId ?? prev.roleId,
        roleName: login.roleName ?? prev.roleName,
        roleLevel: login.roleLevel ?? prev.roleLevel,
        jti: login.jti ?? prev.jti,
        familyId: login.familyId ?? prev.familyId,
        accessToken: login.accessToken ?? prev.accessToken,
        error: null,
      };
    });
  }

  /** Inicializar sesión desde redirección interna (token + data base64). */
  initializeFromRedirect(userData: Partial<LoginEntity>, fallbackToken?: string): void {
    this.updateState((prev) => {
      return {
        ...prev,
        isAuthenticated: true,
        accessToken: userData.accessToken ?? fallbackToken ?? prev.accessToken,
        id: userData.id ?? prev.id,
        username: userData.username ?? prev.username,
        fullName: userData.fullName ?? prev.fullName,
        email: userData.email ?? prev.email,
        isSuperadmin: userData.isSuperadmin ?? prev.isSuperadmin,
        roleType: userData.roleType ?? prev.roleType,
        roleId: userData.roleId ?? prev.roleId,
        roleName: userData.roleName ?? prev.roleName,
        jti: userData.jti ?? prev.jti,
        familyId: userData.familyId ?? prev.familyId,
        error: null,
      };
    });
    this.bootstrapDone = true;
    this.sessionTimeout.start();
  }

  resetForm(): void {
    this.resetState();
  }

  /**
   * Garantiza que la rehidratación de la cookie ya terminó antes de decidir
   * rutas. Los guards la esperan (canActivate asíncrono). Single-flight: los
   * guarding concurrentes comparten la misma promesa. Tras el primer arranque
   * ya no refresca — es el 401 de una petición real quien decide (contrato §3).
   */
  ensureSessionValidated(): Promise<void> {
    if (this.bootstrapDone) return Promise.resolve();
    if (this.validationPromise) return this.validationPromise;

    this.validationPromise = this.refreshSession({ silent: true })
      .then(() => undefined)
      .finally(() => {
        this.bootstrapDone = true;
        this.validationPromise = null;
      });
    return this.validationPromise;
  }

  /** Orquestación completa del login (POST /auth/login, contrato §1). */
  async login(): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    const username = SanitizeUtil.sanitizeInput(this.snapshot().username);
    const password = this.snapshot().password;

    if (!username || !password) {
      this.setError('Usuario y contraseña son requeridos');
      this.setLoading(false);
      return;
    }

    try {
      const response = await this.requestLogin({ username, password });
      const login = response.data;
      if (!login) {
        this.setError('No se recibió una sesión válida del servidor.');
        this.setLoading(false);
        return;
      }

      this.setLoginResult(login);
      this.bootstrapDone = true;
      this.sessionTimeout.start();
      const success = await this.router.navigateByUrl('/viewer');
      this.setLoading(false);
      if (!success) {
        this.setError('No se pudo acceder al visor. Intente nuevamente.');
      }
    } catch (error: any) {
      const message = this.getErrorMessage(error);
      this.setError(message);
      this.setLoading(false);
    }
  }

  /**
   * Renueva la sesión vía POST /auth/refresh (contrato §2). Sin body ni headers:
   * el navegador envía la cookie httponly (withCredentials). Reemplaza el
   * access-token en memoria y devuelve `true` si la sesión es válida.
   *
   * - `silent`: true para la rehidratación de bootstrap (una cookie ausente o
   *   inválida no debe navegar ni tostar); false cuando lo dispara un 401 real.
   *
   * Si el servidor responde un 401 (cookie revocada/vencida) y NO es silencioso,
   * la sesión se descarta y se regresa al login. Los errores transitorios de
   * red preservan la sesión.
   */
  async refreshSession(options?: { silent?: boolean }): Promise<boolean> {
    const silent = options?.silent ?? false;
    const wasAuthenticated = this.snapshot().isAuthenticated;

    try {
      const response = await this.requestRefresh();
      const login = response.data;
      if (!login) return false;
      this.setRefreshResult(login);
      if (!wasAuthenticated) {
        this.sessionTimeout.start();
      }
      return true;
    } catch (error: any) {
      // Solo se regresa al login si HABÍA una sesión que expiró. Un visitante
      // anónimo (p. ej. en el portal o el visor públicos) no debe ser expulsado
      // al login por un 401 de un endpoint protegido.
      if (error?.status === 401 && !silent && wasAuthenticated) {
        this.clearSession();
        if (!this.router.url.startsWith('/login')) {
          await this.router.navigateByUrl('/login', { replaceUrl: true });
        }
      }
      return false;
    }
  }

  /** Cierra la sesión en todos los dispositivos (POST /auth/logout-all). */
  async logoutAll(): Promise<void> {
    try {
      await this.requestLogoutAll();
    } catch {
      // best-effort: la sesión local se limpia de todas formas.
    } finally {
      this.logoutInProgress = false;
      this.finishLogout();
    }
  }

  /** Cierra la sesión actual (POST /auth/logout). */
  async logout(): Promise<void> {
    if (this.logoutInProgress) return;
    this.logoutInProgress = true;
    try {
      await this.requestLogout();
    } catch {
      // best-effort: la sesión local se limpia de todas formas.
    } finally {
      this.logoutInProgress = false;
      this.finishLogout();
    }
  }

  /** Limpieza local sin notificar al servidor (usada en failure paths). */
  clearSession(): void {
    this.sessionTimeout.stop();
    this.resetForm();
    this.sessionCleanup.clear();
  }

  private finishLogout(): void {
    this.sessionTimeout.stop();
    this.resetForm();
    this.sessionCleanup.clear();
    this.router.navigateByUrl('/login', { replaceUrl: true });
  }

  private requestLogin(
    credentials: Pick<LoginEntity, 'username' | 'password'>
  ): Promise<ResponseValueObject<LoginEntity>> {
    return this.repository.login(credentials);
  }

  /** POST /auth/refresh — sin body ni refreshToken: el navegador manda la cookie. */
  private requestRefresh(): Promise<ResponseValueObject<LoginEntity>> {
    return this.repository.refresh();
  }

  private requestLogout(): Promise<ResponseValueObject<void>> {
    return this.repository.logout();
  }

  private requestLogoutAll(): Promise<ResponseValueObject<void>> {
    return this.repository.logoutAll();
  }

  private getErrorMessage(error: any): string {
    // 1) Prefer the backend message.
    const backendMessage = error?.response?.data?.message;
    if (backendMessage) {
      return SanitizeUtil.sanitizeInput(String(backendMessage));
    }

    // 2) Fallback manual messages only when the backend did not provide one.
    if (error?.code === 'ECONNABORTED') {
      return 'La solicitud tardó demasiado. Verifique su conexión e intente nuevamente.';
    }
    if (error?.response?.status === 401) {
      return 'Credenciales inválidas. Verifique su usuario y contraseña.';
    }
    if (error?.response?.status === 403) {
      return 'No tiene permisos para acceder al sistema.';
    }
    if (error?.code === 'ERR_NETWORK') {
      return 'No se puede conectar con el servidor. Verifique su conexión de red.';
    }
    return error?.message ?? 'Error al iniciar sesión. Verifique sus credenciales.';
  }
}